import { Helmet } from 'react-helmet-async';

/**
 * 페이지별 SEO 메타태그 일괄 설정 컴포넌트.
 *
 * - title: <title> + og:title + twitter:title
 * - description: <meta name="description"> + og:description + twitter:description
 * - canonical: 절대 URL 또는 사이트 루트 기준 상대 경로 ('/board/coin' 등)
 * - image: og:image + twitter:image (절대 URL 또는 /... 경로)
 * - type: og:type ('website' | 'article')
 *
 * 미지정 시 index.html 의 정적 fallback 값이 그대로 유지됨.
 */

const SITE_NAME = '시세공간';
const SITE_URL = 'https://community.insidemation.com';

/**
 * OG 이미지 — 상황별 두 종 운용.
 *   - LARGE (1200×630): Open Graph 표준. Facebook/LinkedIn/카카오톡/디스코드/슬랙 등 큰 카드.
 *   - SMALL (800×400):  Twitter Card (summary_large_image) 등 작은 컨테이너 / 저대역폭 환경.
 * og:image 는 다중 선언으로 큰 것 우선 + 작은 것 fallback. twitter:image 는 작은 것 사용.
 */
const DEFAULT_IMAGE_LARGE = `${SITE_URL}/og_1200_630.png`;
const DEFAULT_IMAGE_LARGE_WIDTH = 1200;
const DEFAULT_IMAGE_LARGE_HEIGHT = 630;
const DEFAULT_IMAGE_SMALL = `${SITE_URL}/og_800_400.png`;
const DEFAULT_IMAGE_SMALL_WIDTH = 800;
const DEFAULT_IMAGE_SMALL_HEIGHT = 400;

interface SEOHeadProps {
  title: string;
  description?: string;
  /**
   * - 절대 URL (https://...) → 그대로 사용
   * - '/board/coin' 같은 SPA 경로 → SITE_URL 자동 prefix
   * - 미지정 → canonical 미출력
   */
  canonical?: string;
  /**
   * - 절대 URL → 그대로 사용
   * - '/...' 시작 → SITE_URL prefix
   * - 미지정 → 기본 OG 이미지(og_1200_630.png + og_800_400.png 다중) 사용
   */
  image?: string;
  type?: 'website' | 'article';
}

function toAbsoluteUrl(input: string | undefined, fallback?: string): string | undefined {
  if (!input) return fallback;
  if (/^https?:\/\//i.test(input)) return input;
  if (input.startsWith('/')) return `${SITE_URL}${input}`;
  return `${SITE_URL}/${input}`;
}

export function SEOHead({
  title,
  description,
  canonical,
  image,
  type = 'website',
}: SEOHeadProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = toAbsoluteUrl(canonical);

  /* 페이지에서 커스텀 이미지가 들어오면 그 한 장만 사용 (게시글 첨부 이미지 등).
     커스텀 미지정 시에만 기본 1200×630 + 800×400 다중 OG 노출. */
  const customImageUrl = image ? toAbsoluteUrl(image) : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {customImageUrl ? (
        <meta property="og:image" content={customImageUrl} />
      ) : (
        <>
          {/* 큰 카드 — Facebook/카카오톡 등이 우선 선택 */}
          <meta property="og:image" content={DEFAULT_IMAGE_LARGE} />
          <meta property="og:image:width" content={String(DEFAULT_IMAGE_LARGE_WIDTH)} />
          <meta property="og:image:height" content={String(DEFAULT_IMAGE_LARGE_HEIGHT)} />
          <meta property="og:image:type" content="image/png" />
          {/* 작은 카드 — 작은 컨테이너/저대역폭 클라이언트 fallback */}
          <meta property="og:image" content={DEFAULT_IMAGE_SMALL} />
          <meta property="og:image:width" content={String(DEFAULT_IMAGE_SMALL_WIDTH)} />
          <meta property="og:image:height" content={String(DEFAULT_IMAGE_SMALL_HEIGHT)} />
          <meta property="og:image:type" content="image/png" />
        </>
      )}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ko_KR" />

      {/* Twitter Card — 작은 카드(800×400) 가 트위터 summary_large_image 비율(2:1) 에 가까워 적합 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={customImageUrl ?? DEFAULT_IMAGE_SMALL} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </Helmet>
  );
}
